import { Injectable } from '@angular/core';
import { CelebrationEvent } from '../interfaces/celebration-event.interface';
import { CocoCelebrationEvent } from '../interfaces/coco-celebration-event.interface';
import { Celebration } from '../models/celebration.model';
import { CocoCelebrationAssets } from '../models/coco-celebration-assets.model';

@Injectable()
export class CocoCelebrationService {
  private readonly events: CocoCelebrationEvent[] = [];

  constructor() {
    const today = new Date();
    const activeEvents = this.getEventsForDate(Celebration, today);
    const resolved = this.resolveConflicts(activeEvents);
    this.events = this.mergeWithAssets(resolved);
    this.setCelebrationAttribute();
  }

  /**
   * Returns the mascot path for the highest priority active celebration event.
   * Falls back to lower priority events if the highest priority event doesn't have a mascot.
   * @returns The mascot image path or null if no active celebration exists
   */
  public getMascot() {
    return this.getAssetByPriority(event => event.mascotPath);
  }

  /**
   * Returns the avatar path for the highest priority active celebration event.
   * Falls back to lower priority events if the highest priority event doesn't have an avatar.
   * @returns The avatar image path or null if no active celebration exists
   */
  public getAvatar() {
    return this.getAssetByPriority(event => event.avatarPath);
  }

  /**
   * Returns the type of the highest priority active celebration event.
   * @returns The celebration type string or null if no active celebration exists
   */
  public getType(): CocoCelebrationEvent['type'] | null {
    return this.getAssetByPriority(event => event.type);
  }

  /**
   * Returns the animation for the highest priority active celebration event.
   * Unlike mascot/avatar, animation does NOT fall through — null means "no animation".
   * @returns The animation type or null if no active celebration or no animation configured
   */
  public getAnimation(): CocoCelebrationEvent['globalAnimation'] | null {
    if (!this.events.length) return null;
    const sorted = [...this.events].sort((a, b) => a.priority - b.priority);
    return sorted[0].globalAnimation;
  }

  /**
   * Gets an asset from events ordered by priority.
   * Returns the asset from the first event that has a truthy value for the selector.
   */
  private getAssetByPriority<T>(selector: (event: CocoCelebrationEvent) => T): T | null {
    if (!this.events.length) return null;

    // Sort by priority (lowest number = highest priority)
    const sortedEvents = [...this.events].sort((a, b) => a.priority - b.priority);

    // Find the first event that has the requested asset
    for (const event of sortedEvents) {
      const asset = selector(event);
      if (asset !== null && asset !== undefined) {
        return asset;
      }
    }

    return null;
  }

  /**
   * When multiple events share the same priority, randomly pick one per group.
   * This ensures all assets (mascot, avatar, animation) come from the same event.
   */
  private resolveConflicts(events: CelebrationEvent[]): CelebrationEvent[] {
    const groups = new Map<number, CelebrationEvent[]>();
    for (const event of events) {
      const group = groups.get(event.priority) ?? [];
      group.push(event);
      groups.set(event.priority, group);
    }

    const result: CelebrationEvent[] = [];
    for (const [, group] of groups) {
      if (group.length === 1) {
        result.push(group[0]);
      } else {
        result.push(group[Math.floor(Math.random() * group.length)]);
      }
    }

    return result;
  }

  private getEventsForDate(events: CelebrationEvent[], date: Date): CelebrationEvent[] {
    const month = date.getMonth() + 1; // getMonth() returns 0-11, we need 1-12
    const day = date.getDate();

    return events.filter(event => {
      // Check if current date falls within the event range
      // Handle events that span across year boundary (e.g., Dec 25 - Jan 5)
      if (event.startMonth === event.endMonth) {
        // Same month: simple check
        return month === event.startMonth && day >= event.startDay && day <= event.endDay;
      } else if (event.startMonth < event.endMonth) {
        // Event within same year (e.g., Oct 20 - Nov 5)
        return (
          (month === event.startMonth && day >= event.startDay) ||
          (month > event.startMonth && month < event.endMonth) ||
          (month === event.endMonth && day <= event.endDay)
        );
      }

      // Event spans year boundary (e.g., Dec 20 - Jan 10)
      return (
        (month === event.startMonth && day >= event.startDay) ||
        month > event.startMonth ||
        month < event.endMonth ||
        (month === event.endMonth && day <= event.endDay)
      );
    });
  }

  private setCelebrationAttribute(): void {
    if (typeof document === 'undefined') return;
    const type = this.getType();
    if (type && type !== 'default') {
      document.documentElement.setAttribute('data-celebration', type);
    } else {
      document.documentElement.removeAttribute('data-celebration');
    }
  }

  private mergeWithAssets(events: CelebrationEvent[]): CocoCelebrationEvent[] {
    return events
      .map(event => {
        const asset = CocoCelebrationAssets.find(a => a.type === event.type);
        if (!asset) {
          // No asset found, skip this event gracefully
          return null;
        }

        return {
          ...event,
          mascotPath: asset.mascotPath,
          avatarPath: asset.avatarPath,
          globalAnimation: asset.globalAnimation
        };
      })
      .filter((e): e is CocoCelebrationEvent => e !== null); // remove nulls
  }
}
